import { SxProps, Theme } from "@mui/material";
import { PopoverProps } from "@mui/material/Popover";
import { ClassNameMap } from "@mui/styles";
import { Equal, Expect } from "@simplyk/common";

import { DashboardButtonProps } from "../DashboardButton";
import { InternalClasses } from "../helpers";
import { LinkProps } from "../Link/types";
import { DashboardTypographyProps } from "../Typography";

import { ZeffyPaletteVibe } from "@/common/types/mui";
import { IconElement } from "@/icons/types";

export const infoboxVibeOptions = ["brand", "danger", "warning", "positive", "neutral"] as const;
export type InfoboxVibe = (typeof infoboxVibeOptions)[number];

export const infoboxPointerOptions = [
  "left",
  "right",
  "top",
  "bottom",
  "top-right",
  "top-left",
  "bottom-right",
  "bottom-left",
] as const;
export type InfoboxPointerPosition = (typeof infoboxPointerOptions)[number];

// Make sure that the ButtonVibe type is a subset of the ZeffyPaletteVibe type
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type InfoboxVibeTest = Expect<
  Equal<Extract<ZeffyPaletteVibe, "brand" | "danger" | "warning" | "positive"> | "neutral", InfoboxVibe>
>;

export type InfoboxBaseClasses =
  | "button"
  | "buttons"
  | "buttonsMedium"
  | "container"
  | "iconAndContent"
  | "content"
  | "contentPaddingTop"
  | "close"
  | "closeMedium"
  | "icon"
  | "title"
  | "wrapper"
  | "body"
  | "paper"
  | "actions"
  | "actionsSmall"
  | "linkContainer"
  | "link"
  | "centerIcon"
  | InfoboxVibe;

export type InfoboxPopoverBaseClasses = "popover";

interface InfoboxPopoverProps {
  anchorEl: PopoverProps["anchorEl"];
  open: boolean;
  popoverProps?: Omit<PopoverProps, "anchorEl" | "children" | "onClose" | "open">;
  propegatePopoverOnClose?: boolean;
  type?: "popover";
}

interface InfoboxAlertProps {
  anchorEl?: never;
  open?: never;
  popoverProps?: never;
  propegatePopoverOnClose?: never;
  type?: "alert";
}

export type InfoboxComponentProps = {
  pointer?: InfoboxPointerPosition;
  children?: React.ReactNode;
  className?: string;
  classes?: Partial<ClassNameMap<InfoboxBaseClasses>>;
  icon?: IconElement;
  hideIcon?: boolean;
  centerIcon?: boolean;
  onClose?(): void;
  size?: "small" | "medium";
  style?: React.CSSProperties;
  title?: string;
  vibe?: InfoboxVibe;
  buttonsProps?: (Partial<Omit<DashboardButtonProps, "classes" | "className" | "onClick" | "size" | "style">> & {
    key: string;
    children: NonNullable<DashboardButtonProps["children"]>;
    onClick?: NonNullable<DashboardButtonProps["onClick"]>;
  })[];
  customButton?: React.ReactNode;
  inlineLinkProps?: (Partial<Omit<LinkProps, "classes" | "className" | "size" | "style" | "underline" | "vibe">> & {
    key: string;
    children: NonNullable<LinkProps["children"]>;
  })[];
  "data-test"?: string;
  secondaryButtonVariant?: DashboardButtonProps["variant"];
  slots?: { left?: React.ReactNode; right?: React.ReactNode };
  titleVariant?: DashboardTypographyProps["variant"];
  sx?: SxProps<Theme>;
};

export type InfoboxProps = (InfoboxPopoverProps | InfoboxAlertProps) & InfoboxComponentProps;

export type InfoboxPointerClasses = "root" | InfoboxPointerPosition | InfoboxVibe;

const INFOBOX_PREFIX = "ZeffyInfobox";

export const zeffyInfoboxClasses: InternalClasses<typeof INFOBOX_PREFIX, InfoboxBaseClasses> = {
  wrapper: `${INFOBOX_PREFIX}-wrapper`,
  body: `${INFOBOX_PREFIX}-body`,
  paper: `${INFOBOX_PREFIX}-paper`,
  iconAndContent: `${INFOBOX_PREFIX}-iconAndContent`,
  content: `${INFOBOX_PREFIX}-content`,
  contentPaddingTop: `${INFOBOX_PREFIX}-contentPaddingTop`,
  brand: `${INFOBOX_PREFIX}-brand`,
  danger: `${INFOBOX_PREFIX}-danger`,
  warning: `${INFOBOX_PREFIX}-warning`,
  positive: `${INFOBOX_PREFIX}-positive`,
  neutral: `${INFOBOX_PREFIX}-neutral`,
  icon: `${INFOBOX_PREFIX}-icon`,
  buttons: `${INFOBOX_PREFIX}-buttons`,
  buttonsMedium: `${INFOBOX_PREFIX}-buttonsMedium`,
  button: `${INFOBOX_PREFIX}-button`,
  title: `${INFOBOX_PREFIX}-title`,
  container: `${INFOBOX_PREFIX}-container`,
  close: `${INFOBOX_PREFIX}-close`,
  closeMedium: `${INFOBOX_PREFIX}-closeMedium`,
  actions: `${INFOBOX_PREFIX}-actions`,
  actionsSmall: `${INFOBOX_PREFIX}-actionsSmall`,
  link: `${INFOBOX_PREFIX}-link`,
  linkContainer: `${INFOBOX_PREFIX}-linkContainer`,
  centerIcon: `${INFOBOX_PREFIX}-centerIcon`,
};

export type InfoboxPointerProps = {
  classes?: Partial<ClassNameMap<InfoboxPointerClasses>>;
  vibe: InfoboxVibe;
  position: InfoboxPointerPosition;
};

export const DEFAULT_INFOBOX_VIBE = "brand";
export const DEFAULT_INFOBOX_SIZE = "medium";
export const DEFAULT_INFOBOX_TYPE = "alert";

export type InfoboxSsrProps = InfoboxProps & {
  forwardedRef?: React.Ref<HTMLDivElement>;
};

const POINTER_PREFIX = "InfoboxPointer";

export const internalPointerClasses: InternalClasses<typeof POINTER_PREFIX, InfoboxPointerClasses> = {
  root: `${POINTER_PREFIX}-root`,
  top: `${POINTER_PREFIX}-top`,
  bottom: `${POINTER_PREFIX}-bottom`,
  left: `${POINTER_PREFIX}-left`,
  right: `${POINTER_PREFIX}-right`,
  ["top-right"]: `${POINTER_PREFIX}-top-right`,
  ["top-left"]: `${POINTER_PREFIX}-top-left`,
  ["bottom-right"]: `${POINTER_PREFIX}-bottom-right`,
  ["bottom-left"]: `${POINTER_PREFIX}-bottom-left`,
  brand: `${POINTER_PREFIX}-brand`,
  danger: `${POINTER_PREFIX}-danger`,
  warning: `${POINTER_PREFIX}-warning`,
  positive: `${POINTER_PREFIX}-positive`,
  neutral: `${POINTER_PREFIX}-neutral`,
};

export const internalPopoverClasses: InternalClasses<typeof INFOBOX_PREFIX, InfoboxPopoverBaseClasses> = {
  popover: `${INFOBOX_PREFIX}-popover`,
};
